import type { MyConnectionAccount } from './my-connections-logic';

export const GMAIL_CATEGORY_IDS = [
  'CATEGORY_PERSONAL',
  'CATEGORY_SOCIAL',
  'CATEGORY_PROMOTIONS',
  'CATEGORY_UPDATES',
  'CATEGORY_FORUMS',
] as const;

export type GmailCategoryId = typeof GMAIL_CATEGORY_IDS[number];
export type CalendarMode = 'off' | 'events_only' | 'goals';

export interface ProviderDiscoveryItem {
  id: string;
  name: string;
  type?: 'system' | 'user';
  primary?: boolean;
  access_role?: string;
}

export type ProviderDiscovery =
  | { status: 'idle' | 'loading'; items: ProviderDiscoveryItem[] }
  | { status: 'available'; items: ProviderDiscoveryItem[] }
  | { status: 'degraded'; reason: 'provider_unavailable'; items: ProviderDiscoveryItem[] };

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? [...new Set(value.filter((item): item is string => typeof item === 'string').map((item) => item.trim()).filter(Boolean))]
    : [];
}

export function normalizeEmailAddresses(value: string | readonly string[]): string[] {
  const entries: readonly string[] = typeof value === 'string' ? value.split(/[\s,、;]+/) : value;
  return [...new Set(entries.map((entry) => entry.trim().toLowerCase()).filter(Boolean))];
}

export interface GmailFilterDraft {
  include_categories: GmailCategoryId[];
  exclude_senders: string[];
  exclude_recipients: string[];
  exclude_labels: string[];
}

export function toggleGmailCategory(
  current: readonly GmailCategoryId[],
  category: GmailCategoryId,
  checked: boolean,
): GmailCategoryId[] {
  if (!checked && current.length <= 1) return [...current];
  return checked
    ? [...new Set([...current, category])]
    : current.filter((item) => item !== category);
}

export function gmailFilterDraft(syncRules: unknown, account: MyConnectionAccount): GmailFilterDraft {
  const globalGmail = record(record(syncRules).gmail);
  const accountGmail = record(record(account.ingest_policy).gmail);
  const canonicalOrLegacy = (field: string): unknown => Object.prototype.hasOwnProperty.call(accountGmail, field)
    ? accountGmail[field] : globalGmail[field];
  const configuredCategories = strings(canonicalOrLegacy('include_categories')).filter((value): value is GmailCategoryId =>
      (GMAIL_CATEGORY_IDS as readonly string[]).includes(value),
    );
  return {
    // Runtime treats unset/[] as the main inbox only (sync-rule-resolver
    // DEFAULT_GMAIL_INCLUDE_CATEGORIES), so show exactly that as checked.
    include_categories: configuredCategories.length > 0 ? configuredCategories : ['CATEGORY_PERSONAL'],
    exclude_senders: normalizeEmailAddresses(strings(canonicalOrLegacy('exclude_senders'))),
    exclude_recipients: normalizeEmailAddresses(strings(canonicalOrLegacy('exclude_recipients'))),
    exclude_labels: strings(canonicalOrLegacy('exclude_labels')),
  };
}

export function gmailGlobalPatch(syncRules: unknown, draft: GmailFilterDraft): Record<string, unknown> {
  const globalGmail = record(record(syncRules).gmail);
  return {
    ...globalGmail,
    include_categories: draft.include_categories,
    exclude_senders: normalizeEmailAddresses(draft.exclude_senders),
    exclude_recipients: normalizeEmailAddresses(draft.exclude_recipients),
  };
}

export function calendarModesForAccount(account: MyConnectionAccount): Record<string, { mode: CalendarMode }> {
  const policy = record(account.ingest_policy);
  const calendar = record(policy.calendar);
  const calendars = record(calendar.calendars);
  if (Object.prototype.hasOwnProperty.call(calendar, 'calendars')) {
    return Object.fromEntries(Object.entries(calendars).flatMap(([id, raw]) => {
      const mode = record(raw).mode;
      return mode === 'off' || mode === 'events_only' || mode === 'goals' ? [[id, { mode }]] : [];
    }));
  }
  return Object.fromEntries(strings(policy.include_calendar_ids).map((id) => [id, { mode: 'goals' as const }]));
}

export function mergeDiscoveredIds(
  items: readonly ProviderDiscoveryItem[],
  savedIds: readonly string[],
): ProviderDiscoveryItem[] {
  const discovered = new Map(items.map((item) => [item.id, item]));
  for (const id of savedIds) {
    if (!discovered.has(id)) discovered.set(id, { id, name: id });
  }
  return [...discovered.values()];
}
